// AD-2698 (Maria, 14 October 2026): nobody can see anything in a report that they cannot see in the profiles. Each
// report detail belongs to the profile section that shows it, and is offered only where the person's role can view
// that section in Manage Access for students, applicants or enquiries. The server makes the same check.

export const REPORT_ACCESS_LINE =
    'You can only report on information your role has permission to view.';

const STUDENT_FIELD_SECTIONS = {
    LegalName: 'Profile',
PreferredName: 'Profile',
ProfileStatus: 'Profile',
Sex: 'Profile',
BoarderStatus: 'Profile',
    SchoolGroups: 'Profile',
YearGroup: 'Profile',
Form: 'Profile',
House: 'Profile',
IntakeGroup: 'Profile',
    ApplicationStatus: 'Profile',
Upn: 'Profile',
PreviousSchoolName: 'Profile',
PreviousSchoolEmail: 'Profile',
    DateOfBirth: 'Profile',
Address: 'Profile',
Postcode: 'Profile',
    // AD-2228: the applicant's distance from the school, with the address
    DistanceFromSchool: 'Profile',
    ParentContact1: 'Contacts',
ParentContact2: 'Contacts',
    OtcMedicine: 'MedicalConsents',
    SenStatus: 'SEN',
Sen: 'SEN',
    DietaryRequirements: 'DietaryNeedsAndFoodAllergies',
    Welfare: 'Welfare',
    FreeSchoolMeals: 'FreeSchoolMealsEligibility',
    // AD-2821: the same section as Free School Meals
    ServiceChildYoungCarer: 'FreeSchoolMealsEligibility',
    Ethnicity: 'Ethnicity',
FirstLanguageExposedTo: 'Ethnicity',
SpeaksEnglish: 'Ethnicity',
BirthCountry: 'Ethnicity',
    Religion: 'Ethnicity',
LanguageSpokenAtHome: 'Ethnicity',
AsylumStatus: 'Ethnicity',
Nationality: 'Ethnicity',
    Consents: 'Consents',
    TravelOptions: 'TravelMode',
    AdmissionCategories: 'AdmissionCategories',
    MedicalConditions: 'MedicalConditions',
};

const EMPLOYEE_FIELD_SECTIONS = {
    LegalName: 'StaffBasicInformation',
PreferredName: 'StaffBasicInformation',
Title: 'StaffBasicInformation',
    DateOfBirth: 'StaffBasicInformation',
Email: 'StaffBasicInformation',
MobileNumber: 'StaffBasicInformation',
    NINumber: 'StaffIdentificationAndBanking',
BankDetails: 'StaffIdentificationAndBanking',
    HomeAddress: 'StaffHomeAddress',
Postcode: 'StaffHomeAddress',
    CarDetails: 'StaffCars',
NextOfKin: 'StaffNextOfKin',
Contacts: 'StaffContacts',
};

const canView = (permissions = [], module) => permissions.includes(`Permission.${module}.View`);

export const PROFILE_KINDS = ['Student', 'Applicant', 'Enquiry'];

// AD-2727: a detail is offered when the person can view its section for at least one kind of child in the report, so a
// report on children on roll does not offer a section set to Hide for students.
export const canViewStudentField = (permissions, field, kinds = PROFILE_KINDS) => {
    const section = STUDENT_FIELD_SECTIONS[field];
    return Boolean(section) && kinds.some(kind => canView(permissions, `${kind}${section}`));
};

// The kinds of child a report covers: from the children chosen one by one, and from the part of the Profiles page each
// group is under. A school group can contain any child, as can a group saved before the part was recorded.
const KIND_OF_PART = {'ON-ROLL': 'Student', 'PRE-ADMISSION': 'Applicant', 'FUTURE-ADMISSION': 'Applicant', ENQUIRY: 'Enquiry'};

export const kindsInReport = settings => {
    const kinds = new Set();
    if (settings.studentFilters?.length) kinds.add('Student');
    if (settings.applicationFilters?.length) kinds.add('Applicant');
    if (settings.enquiryFilters?.length) kinds.add('Enquiry');
    (settings.groupFilters || []).filter(group => !group.excluded).forEach(group => {
        const kind = KIND_OF_PART[(group.part || '').toUpperCase()];
        (kind ? [kind] : PROFILE_KINDS).forEach(each => kinds.add(each));
    });
    return kinds.size ? PROFILE_KINDS.filter(kind => kinds.has(kind)) : PROFILE_KINDS;
};

export const canViewEmployeeField = (permissions, field) => {
    const section = EMPLOYEE_FIELD_SECTIONS[field];
    return Boolean(section) && canView(permissions, section);
};

export const canRunMedicineAtSchoolReport = permissions => canView(permissions, 'StudentMedicines');

export const canRunMedicalEventsReport = permissions =>
    canView(permissions, 'StudentMedicalEvents') || canView(permissions, 'ApplicantMedicalEvents');
